import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { LogOut, LayoutDashboard, Building2, CalendarDays, Menu, User, Settings, BarChart3, MessageSquare, CalendarClock, FileCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { NavLink } from "@/components/NavLink";
import { NotificationDropdown } from "@/components/NotificationDropdown";
import { KeyboardShortcutsOverlay } from "@/components/KeyboardShortcutsOverlay";
import { UserMenu } from "./UserMenu";

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [userEmail, setUserEmail] = useState<string>("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const getUserEmail = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
      }
    };
    getUserEmail();
  }, []);

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Erreur",
        description: "Impossible de se déconnecter",
        variant: "destructive",
      });
    } else {
      setMobileMenuOpen(false);
      navigate("/auth");
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Main nav items - 5 core items (Stats hidden for now)
  const mainNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/bookings", label: "Réservations", icon: CalendarDays },
    { path: "/messages", label: "Messages", icon: MessageSquare },
    { path: "/operations", label: "Opérations", icon: CalendarClock },
    { path: "/properties", label: "Propriétés", icon: Building2 },
    // { path: "/analytics", label: "Stats", icon: BarChart3 }, // Hidden for now
  ];

  const profileItem = { path: "/profile", label: "Profil", icon: User };

  // Hide bottom nav on immersive pages like messages
  const hideBottomNav = location.pathname.startsWith("/messages");

  return (
    <>
      <KeyboardShortcutsOverlay />
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-14 flex items-center justify-between gap-4">
          {/* Mobile: Hamburger left, Logo centered, Actions right */}
          <div className="md:hidden flex items-center justify-between w-full">
            {/* Left: Hamburger */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10 touch-manipulation">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] sm:w-[320px]">
                <SheetHeader>
                  <SheetTitle className="font-playfair text-xl">
                    Stay Properties
                  </SheetTitle>
                </SheetHeader>

                <div className="mt-6 space-y-6">
                  {/* User Info */}
                  {userEmail && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">Compte</p>
                        <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Navigation Links */}
                  <nav className="space-y-1">
                    {mainNavItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path || 
                        (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
                      
                      return (
                        <button
                          key={item.path}
                          onClick={() => handleNavigation(item.path)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors touch-manipulation ${
                            isActive 
                              ? "bg-accent text-accent-foreground font-medium" 
                              : "hover:bg-accent/50"
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                          {item.label}
                        </button>
                      );
                    })}
                  </nav>

                  <Separator />

                  {/* Profile Link */}
                  <button
                    onClick={() => handleNavigation(profileItem.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors touch-manipulation ${
                      location.pathname === profileItem.path
                        ? "bg-accent text-accent-foreground font-medium" 
                        : "hover:bg-accent/50"
                    }`}
                  >
                    <User className="h-5 w-5" />
                    {profileItem.label}
                  </button>

                  <Separator />

                  {/* Settings Section */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-3 px-3 py-2.5">
                      <Settings className="h-5 w-5 text-muted-foreground" />
                      <span className="text-sm font-medium">Paramètres</span>
                    </div>
                    
                    <div className="px-3 py-2 flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Thème</span>
                      <ThemeToggle />
                    </div>
                  </div>

                  <Separator />

                  {/* Sign Out */}
                  <Button
                    variant="ghost"
                    onClick={handleSignOut}
                    className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <LogOut className="h-5 w-5" />
                    Déconnexion
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            {/* Center: Logo */}
            <button 
              onClick={() => navigate('/dashboard')}
              className="font-playfair text-lg font-bold hover:opacity-80 transition-opacity absolute left-1/2 -translate-x-1/2"
            >
              Stay Properties
            </button>

            {/* Right: Notifications */}
            <NotificationDropdown />
          </div>

          {/* Desktop/Tablet: Logo left */}
          <div className="hidden md:flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="font-playfair text-lg font-bold hover:opacity-80 transition-opacity"
            >
              Stay Properties
            </button>
          </div>

          {/* Desktop: Centered Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5 absolute left-1/2 transform -translate-x-1/2">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/dashboard"}
                  className="px-3 py-1.5 text-sm rounded-md hover:bg-accent/50 transition-colors flex items-center gap-1.5"
                  activeClassName="bg-accent text-accent-foreground font-medium"
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* Tablet: Centered Compact nav with icons only */}
          <nav className="hidden md:flex lg:hidden items-center gap-0.5 absolute left-1/2 transform -translate-x-1/2">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/dashboard"}
                  className="px-2 py-2 text-sm rounded-md hover:bg-accent/50 transition-colors"
                  activeClassName="bg-accent text-accent-foreground font-medium"
                >
                  <Icon className="h-5 w-5" />
                </NavLink>
              );
            })}
          </nav>

          {/* Desktop & Tablet Actions */}
          <div className="hidden md:flex items-center gap-1">
            <NotificationDropdown />
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>

        {/* Mobile Bottom Nav - Hidden on immersive pages, 5 items max for better touch targets */}
        {!hideBottomNav && (
          <nav className="md:hidden border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container mx-auto px-1 py-1 pb-safe flex justify-around">
              <NavLink
                to="/dashboard"
                end
                className="flex flex-col items-center justify-center gap-0.5 min-w-[52px] min-h-[44px] py-1 text-[10px] rounded-lg hover:bg-accent/50 transition-colors touch-manipulation"
                activeClassName="bg-accent text-accent-foreground font-medium"
              >
                <LayoutDashboard className="h-5 w-5" />
                <span>Home</span>
              </NavLink>
              <NavLink
                to="/messages"
                className="flex flex-col items-center justify-center gap-0.5 min-w-[52px] min-h-[44px] py-1 text-[10px] rounded-lg hover:bg-accent/50 transition-colors touch-manipulation"
                activeClassName="bg-accent text-accent-foreground font-medium"
              >
                <MessageSquare className="h-5 w-5" />
                <span>Messages</span>
              </NavLink>
              <NavLink
                to="/bookings"
                className="flex flex-col items-center justify-center gap-0.5 min-w-[52px] min-h-[44px] py-1 text-[10px] rounded-lg hover:bg-accent/50 transition-colors touch-manipulation"
                activeClassName="bg-accent text-accent-foreground font-medium"
              >
                <CalendarDays className="h-5 w-5" />
                <span>Bookings</span>
              </NavLink>
              <NavLink
                to="/operations"
                className="flex flex-col items-center justify-center gap-0.5 min-w-[52px] min-h-[44px] py-1 text-[10px] rounded-lg hover:bg-accent/50 transition-colors touch-manipulation"
                activeClassName="bg-accent text-accent-foreground font-medium"
              >
                <CalendarClock className="h-5 w-5" />
                <span>Ops</span>
              </NavLink>
              <NavLink
                to="/properties"
                className="flex flex-col items-center justify-center gap-0.5 min-w-[52px] min-h-[44px] py-1 text-[10px] rounded-lg hover:bg-accent/50 transition-colors touch-manipulation"
                activeClassName="bg-accent text-accent-foreground font-medium"
              >
                <Building2 className="h-5 w-5" />
                <span>Props</span>
              </NavLink>
            </div>
          </nav>
        )}
      </header>
    </>
  );
};
